/**
 * Medallia DXA (Decibel Insight) session-recording control for the Admin
 * area (CD-8245, Group 4 / REVISION 12).
 *
 * **APPROVED EXCEPTION** to the oso-spa "analytics is dataLayer-only; never
 * call `decibelInsight` from the frontend" rule (see
 * `documentation/context/medallia-dxa-integration.md`). Every other DXA/GTM
 * interaction in this codebase MUST go through `window.dataLayer` — this
 * module is a deliberate, CD-8245-specific exception for recording
 * **control** only (pause/resume), never for event tracking. It exists
 * because GTM/dataLayer dispatch is asynchronous, and the pause must be
 * issued synchronously, before the Admin view paints, to guarantee nothing
 * in `/admin*` is ever captured.
 *
 * Rather than masking individual Admin fields (as the rest of CD-8245 does
 * for the caseworker/consumer views), DXA session recording is paused for
 * the entire `/admin*` section and resumed everywhere else. See the route
 * finding in the CD-8245 plan: caseworker/consumer views live under
 * `/complaints`, `/apply`, `/referral`, `/downloads` — NOT `/admin` — so this
 * module has no bearing on the field-level masking elsewhere in the app.
 *
 * The DXA global (`window.decibelInsight`, loaded via GTM and consent-gated)
 * may legitimately be absent (tag not fired yet, consent not given, ad
 * blocker, etc.) — every export here MUST no-op safely in that case and must
 * never throw.
 */

declare global {
  interface Window {
    decibelInsight?: (command: string, ...args: unknown[]) => void;
  }
}

const isDxaAvailable = (): boolean =>
  typeof window !== 'undefined' && typeof window.decibelInsight === 'function';

/**
 * Pauses DXA session recording. Safe no-op if the DXA global is absent.
 *
 * Idempotent: pausing an already-paused recorder is harmless (DXA handles
 * it), so no local state is tracked here beyond the current path.
 */
export const pauseDxaRecording = (): void => {
  if (isDxaAvailable()) {
    window.decibelInsight!('pauseRecording');
  }
};

/**
 * Resumes DXA session recording. Safe no-op if the DXA global is absent.
 *
 * Idempotent: resuming an already-recording session is harmless.
 */
export const resumeDxaRecording = (): void => {
  if (isDxaAvailable()) {
    window.decibelInsight!('resumeRecording');
  }
};

/**
 * True when `pathname`'s first path segment is `admin` (`/admin` or
 * `/admin/...`). Deliberately a first-segment prefix match, NOT a naive
 * `includes('admin')` — `/administrators` and `/complaints/admin-notes`
 * must NOT match.
 *
 * There is no router `basename` configured today (verified in
 * `configureApp.js` / `history/createBrowserHistory`), so `pathname` is
 * assumed to be the full app-relative path. If a `basename` is ever
 * introduced, this check would need to account for it.
 */
export const isAdminPath = (pathname: string): boolean => {
  const [firstSegment] = pathname.split('/').filter(Boolean);

  return firstSegment === 'admin';
};

/**
 * Pauses DXA recording on any `/admin*` route, resumes it everywhere else.
 * Intended to be driven by the app's router/history on every navigation
 * (see `configureApp.js`), plus once for the initial location so a direct
 * load onto `/admin` is covered.
 */
export const applyDxaRecordingForPath = (pathname: string): void => {
  if (isAdminPath(pathname)) {
    pauseDxaRecording();
  } else {
    resumeDxaRecording();
  }
};
